import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// Auth0 Universal Login in a popup, shared by every "Log in" / "Sign up" affordance.
// The popup is the whole flow — no intermediate page — so callers just render a
// button and call openAuthPopup('login' | 'signup').
//
// The popup ends on /api/auth/popup-callback, which is served by the BACKEND
// origin. When the API lives on its own domain (dev: api.wallpass.app vs
// demo.wallpass.app) its postMessage arrives from that origin, so both the app
// origin and the API origin are accepted. If the message is lost entirely
// (blocked opener, manual close), the popup.closed poll re-checks the session.
const API_ORIGIN = (() => {
  const base = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:3000';
  if (!base) return window.location.origin; // same-origin deploys build with ''
  try {
    return new URL(base, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;
const CLOSE_POLL_MS = 500;

const popupFeatures = () => {
  const left = window.screenX + Math.max(0, (window.outerWidth - POPUP_WIDTH) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - POPUP_HEIGHT) / 2);
  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${Math.round(left)},top=${Math.round(top)}`;
};

export const useAuthPopup = () => {
  const { refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Picks up the session the popup established, without a full page reload.
  const finish = useCallback(async () => {
    stopPolling();
    setIsLoading(false);
    await refreshUser();
  }, [refreshUser, stopPolling]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin && event.origin !== API_ORIGIN) return;
      if (event.data?.popupComplete) {
        finish();
      } else if (event.data?.popupError) {
        stopPolling();
        setIsLoading(false);
        setError('Sign in failed. Please try again.');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [finish, stopPolling]);

  const openAuthPopup = useCallback(
    async (mode = 'login') => {
      setError('');
      setIsLoading(true);
      try {
        const params = mode === 'signup' ? { screen_hint: 'signup' } : {};
        const { data } = await api.get('/auth/popup-init', { params });

        const popup = window.open(data.authorizationUrl, 'wallpass_auth', popupFeatures());
        if (!popup) {
          setError('Popup blocked. Please allow popups for this site.');
          setIsLoading(false);
          return;
        }
        popup.focus?.();

        stopPolling();
        pollRef.current = setInterval(() => {
          if (popup.closed) finish();
        }, CLOSE_POLL_MS);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            `Failed to start ${mode === 'signup' ? 'sign up' : 'log in'}. Please try again.`
        );
        setIsLoading(false);
      }
    },
    [finish, stopPolling]
  );

  return { openAuthPopup, isLoading, error, setError };
};

export default useAuthPopup;
